import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { webhookApi } from '../../services/api';
import { PageHeader, Spinner, Modal, ConfirmModal, EmptyState } from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  PlusIcon,
  PaperAirplaneIcon,
  ListBulletIcon,
  TrashIcon,
  ShieldCheckIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';

const WEBHOOK_EVENTS = [
  'review.submitted',
  'rating.finalized',
  'pip.created',
  'pip.closed',
  'override.applied',
  'goal.updated',
  'survey.completed',
  'cycle.scored',
  'webhook.test',
];

function relativeTime(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function truncateUrl(url) {
  if (!url) return '';
  if (url.length <= 48) return url;
  return url.slice(0, 28) + '…' + url.slice(-16);
}

function NewSubscriptionForm({ saving, onClose, onSave }) {
  const [name, setName] = useState('');
  const [url, setUrl]   = useState('');
  const [events, setEvents] = useState([]);

  const toggle = (ev) => setEvents(list => list.includes(ev) ? list.filter(e => e !== ev) : [...list, ev]);

  const valid = name.trim() && /^https?:\/\//.test(url) && events.length > 0;

  return (
    <div className="space-y-4">
      <div>
        <label className="label">Name</label>
        <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Production events" />
      </div>
      <div>
        <label className="label">Endpoint URL</label>
        <input className="input" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://api.example.com/hooks/calibza" />
      </div>
      <div>
        <label className="label">Events</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-56 overflow-auto border border-gray-100 rounded-lg p-2">
          {WEBHOOK_EVENTS.map(ev => (
            <label key={ev} className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={events.includes(ev)} onChange={() => toggle(ev)} className="w-4 h-4 rounded border-gray-300" />
              <span className="text-sm text-gray-700 font-mono">{ev}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button disabled={!valid || saving} onClick={() => onSave({ name: name.trim(), url: url.trim(), events })} className="btn-primary disabled:opacity-40">
          {saving ? 'Creating…' : 'Create subscription'}
        </button>
      </div>
    </div>
  );
}

function SecretBanner({ secret, onClose }) {
  const copy = () => {
    navigator.clipboard?.writeText(secret).then(() => toast.success('Secret copied'));
  };
  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
        <p className="font-semibold mb-1">Copy this signing secret now.</p>
        <p>It won't be shown again. Use it to verify the <code className="font-mono text-xs bg-amber-100 px-1 rounded">X-Calibza-Signature</code> header on incoming webhook requests.</p>
      </div>
      <div className="flex items-center gap-2 bg-gray-900 text-gray-100 rounded-lg p-3 font-mono text-xs break-all">
        <span className="flex-1">{secret}</span>
        <button onClick={copy} className="p-1.5 hover:bg-gray-800 rounded shrink-0" title="Copy">
          <ClipboardDocumentIcon className="w-4 h-4" />
        </button>
      </div>
      <div className="flex justify-end">
        <button onClick={onClose} className="btn-primary">I've saved the secret</button>
      </div>
    </div>
  );
}

function DeliveriesModal({ webhookId, open, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ['webhook-deliveries', webhookId],
    enabled:  open && !!webhookId,
    queryFn:  () => webhookApi.deliveries(webhookId).then(r => r.data),
  });

  const deliveries = data?.data || data || [];

  return (
    <Modal open={open} onClose={onClose} title="Recent deliveries" size="lg">
      {isLoading ? (
        <div className="flex justify-center p-10"><Spinner /></div>
      ) : deliveries.length === 0 ? (
        <EmptyState title="No deliveries yet" description="Deliveries will appear here once this webhook fires." />
      ) : (
        <div className="max-h-[60vh] overflow-auto">
          <table className="table">
            <thead>
              <tr>
                {['Event','Status','Attempt','Delivered','Error'].map(h => <th key={h} className="th">{h}</th>)}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {deliveries.slice(0, 100).map(d => (
                <tr key={d.id} className="tr-hover">
                  <td className="td text-xs font-mono">{d.event}</td>
                  <td className="td">
                    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                      d.status_code >= 200 && d.status_code < 300 ? 'bg-green-100 text-green-700'
                      : d.status_code ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600')}>
                      {d.status_code || 'pending'}
                    </span>
                  </td>
                  <td className="td text-xs">{d.attempt || 1}</td>
                  <td className="td text-xs text-gray-500">{relativeTime(d.delivered_at)}</td>
                  <td className="td text-xs text-red-600 max-w-xs truncate" title={d.error || ''}>{d.error || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

export default function Webhooks() {
  const qc = useQueryClient();
  const [creating, setCreating]   = useState(false);
  const [secret, setSecret]       = useState(null);
  const [deleting, setDeleting]   = useState(null);
  const [viewingDeliveries, setViewingDeliveries] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['webhooks'],
    queryFn:  () => webhookApi.list().then(r => r.data),
  });
  const hooks = data?.data || data || [];

  const createMut = useMutation({
    mutationFn: (payload) => webhookApi.create(payload),
    onSuccess:  (r) => {
      toast.success('Subscription created');
      qc.invalidateQueries(['webhooks']);
      setCreating(false);
      const s = r.data?.secret || r.data?.signing_secret;
      if (s) setSecret(s);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to create'),
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, is_active }) => webhookApi.update(id, { is_active }),
    onSuccess:  () => qc.invalidateQueries(['webhooks']),
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to update'),
  });

  const testMut = useMutation({
    mutationFn: (id) => webhookApi.test(id),
    onSuccess:  (r) => {
      const code = r.data?.status_code;
      if (code && code >= 200 && code < 300) toast.success(`Test delivered (${code})`);
      else toast.error(`Test returned ${code || 'error'}`);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Test failed'),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => webhookApi.delete(id),
    onSuccess:  () => { toast.success('Subscription deleted'); qc.invalidateQueries(['webhooks']); setDeleting(null); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to delete'),
  });

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Webhooks"
        subtitle="Outbound event subscriptions for your systems"
        actions={<button onClick={() => setCreating(true)} className="btn-primary"><PlusIcon className="w-4 h-4" />New subscription</button>}
      />

      <div className="card p-4 mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 flex items-start gap-3">
        <ShieldCheckIcon className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <p className="font-semibold">Signed with HMAC-SHA256.</p>
          <p className="text-blue-800 mt-0.5">CalibZa signs each body using your subscription's secret. Verify the <code className="font-mono text-xs bg-blue-100 px-1 rounded">X-Calibza-Signature</code> header on every incoming request before trusting the payload.</p>
        </div>
      </div>

      {hooks.length === 0 ? (
        <div className="card">
          <EmptyState
            title="No subscriptions yet"
            description="Create one to receive real-time events whenever reviews, ratings, or cycles change state."
            action={<button onClick={() => setCreating(true)} className="btn-primary"><PlusIcon className="w-4 h-4" />New subscription</button>}
          />
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                {['Name','URL','Events','Status','Last fired','Actions'].map(h => <th key={h} className="th">{h}</th>)}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {hooks.map(h => {
                const evs = h.events || [];
                return (
                  <tr key={h.id} className="tr-hover">
                    <td className="td text-sm font-medium text-gray-900">{h.name}</td>
                    <td className="td text-xs font-mono text-gray-600" title={h.url}>{truncateUrl(h.url)}</td>
                    <td className="td text-xs">
                      <div className="flex flex-wrap gap-1">
                        {evs.slice(0, 2).map(ev => <span key={ev} className="bg-gray-100 text-gray-700 rounded px-1.5 py-0.5 font-mono">{ev}</span>)}
                        {evs.length > 2 && <span className="text-gray-400">+{evs.length - 2} more</span>}
                      </div>
                    </td>
                    <td className="td">
                      <button onClick={() => toggleMut.mutate({ id: h.id, is_active: !h.is_active })}
                        className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium transition-colors',
                          h.is_active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}>
                        {h.is_active ? 'Active' : 'Disabled'}
                      </button>
                    </td>
                    <td className="td text-xs text-gray-500">{relativeTime(h.last_fired_at)}</td>
                    <td className="td">
                      <div className="flex items-center gap-2">
                        <button onClick={() => testMut.mutate(h.id)} disabled={testMut.isPending} className="btn-secondary btn-sm disabled:opacity-40" title="Send test event">
                          <PaperAirplaneIcon className="w-3.5 h-3.5" /> Test
                        </button>
                        <button onClick={() => setViewingDeliveries(h.id)} className="btn-secondary btn-sm" title="View deliveries">
                          <ListBulletIcon className="w-3.5 h-3.5" /> Log
                        </button>
                        <button onClick={() => setDeleting(h)} className="btn-danger btn-sm" title="Delete">
                          <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={creating} onClose={() => setCreating(false)} title="New webhook subscription" size="md">
        <NewSubscriptionForm
          saving={createMut.isPending}
          onClose={() => setCreating(false)}
          onSave={(data) => createMut.mutate(data)}
        />
      </Modal>

      <Modal open={!!secret} onClose={() => setSecret(null)} title="Signing secret" size="md">
        {secret && <SecretBanner secret={secret} onClose={() => setSecret(null)} />}
      </Modal>

      <DeliveriesModal
        webhookId={viewingDeliveries}
        open={!!viewingDeliveries}
        onClose={() => setViewingDeliveries(null)}
      />

      <ConfirmModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMut.mutate(deleting.id)}
        title={deleting ? `Delete "${deleting.name}"?` : ''}
        message="This permanently removes the subscription. Deliveries in flight may still be attempted before they drain."
        danger
      />
    </div>
  );
}
